/**
 * DATCalculateExpert.jsx — Embeds DAT SilverDAT calculateExpert into the case editor.
 * --------------------------------------------------------------------
 * Workflow (Auto-Fill EG → DAT):
 *   1. User opens the "Kalkulation"-Step → we fetch DAT login info +
 *      the workshop hourly rates (from system_settings or per-case
 *      override) and show them in an editable panel.
 *   2. User clicks "Vorgang in DAT anlegen + Iframe öffnen":
 *        a) Backend POST /api/cases/{id}/dat/create-contract → DAT SOAP
 *           createContract106 → returns `cid` (the DAT contract ID).
 *        b) Backend POST /api/dat/sso-launch → returns auto-submit form.
 *        c) Iframe srcDoc auto-submits SSO → lands on eventList.html →
 *           after iframe-load we set iframe.src to
 *           `contractOpening.tmpl?cid=<cid>` (cookie is now set, so DAT
 *           opens the contract with all pre-filled data).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Calculator, RefreshCcw, AlertTriangle, Maximize2, Wrench, CheckCircle2, Settings2, ExternalLink, Download, FileText, Upload } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import DATCopyHelper from "@/components/DATCopyHelper";
import { useAuth } from "@/lib/auth";

export default function DATCalculateExpert({ caseId, vehicle }) {
  const { user } = useAuth();
  // Kostenkontrolle: Nur Admin & Backoffice dürfen einen DAT-calculateExpert-
  // Vorgang anlegen (jeder Vorgang verursacht laufende DAT-Kosten beim
  // Anbieter). Gutachter/Franchise sehen den Button als gesperrt.
  const canLaunch = user?.role === "admin" || user?.role === "backoffice";
  const [loading, setLoading] = useState(false);
  const [iframeDoc, setIframeDoc] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [loginInfo, setLoginInfo] = useState(null);
  const [defaultRates, setDefaultRates] = useState(null);
  const [caseRates, setCaseRates] = useState({ mechanik: "", karosserie: "", lackierung: "", elektrik: "" });
  const [contractInfo, setContractInfo] = useState(null);  // {contract_id, contract_url, reused, deep_links}
  const [showRates, setShowRates] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [activeStep, setActiveStep] = useState("calculation");  // welcher DAT-Step ist im Iframe geladen
  const [caseDoc, setCaseDoc] = useState(null);  // volle Akte für Copy-Helper
  const [calcPdf, setCalcPdf] = useState(null);  // aktuell angehängte DAT-Kalkulations-PDF (case.documents-Eintrag)
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const calcPdfInputRef = useRef(null);
  const iframeRef = useRef(null);
  const targetUrlRef = useRef(null);
  const navigatedRef = useRef(false);

  // Pre-fetch DAT-Credentials info + default rates + persisted per-case rates.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: info }, { data: r }, { data: caseDoc }] = await Promise.all([
          api.get("/dat/login-info"),
          api.get("/dat/hourly-rates"),
          api.get(`/cases/${caseId}`),
        ]);
        if (cancelled) return;
        setLoginInfo(info);
        setDefaultRates(r);
        const c = caseDoc?.case || {};
        setCaseDoc(c);  // volle Akte für Copy-Helper
        // Jüngste DAT-Kalkulations-PDF (nach uploaded_at sortiert) heraussuchen.
        const dcPdf = (c.documents || [])
          .filter((d) => d.doc_type === "dat_calculation")
          .sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""))[0] || null;
        setCalcPdf(dcPdf);
        const override = c.dat_hourly_rates || {};
        setCaseRates({
          mechanik: override.mechanik ?? r.mechanik ?? "",
          karosserie: override.karosserie ?? r.karosserie ?? "",
          lackierung: override.lackierung ?? r.lackierung ?? "",
          elektrik: override.elektrik ?? r.elektrik ?? r.mechanik ?? "",
        });
        if (c.dat_contract_id) {
          const env = info?.environment || "production";
          const host = env === "production" ? "www.dat.de" : "gold.dat.de";
          setContractInfo({
            contract_id: c.dat_contract_id,
            contract_url: `https://${host}/VehicleRepairOnline/calculateExpert/contractOpening.tmpl?cid=${c.dat_contract_id}`,
            reused: true,
          });
        }
      } catch {
        /* not configured — silent */
      }
    })();
    return () => { cancelled = true; };
  }, [caseId]);

  const escapeHTML = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({"&":"&amp;","<":"&lt;",">":"&gt;","\"":"&quot;","'":"&#39;"}[c]));

  /**
   * Full launch:
   *  1) Create DAT contract from EG case (auto-fill)
   *  2) Run SSO + auto-submit form
   *  3) After SSO iframe loads, navigate to contractOpening.tmpl?cid=...
   */
  const launch = useCallback(async (forceNewContract = false) => {
    setLoading(true);
    setError(null);
    navigatedRef.current = false;
    try {
      // — Step 1: create or reuse DAT contract ─────────────────────
      const ratesPayload = {
        mechanik: parseFloat(caseRates.mechanik) || 0,
        karosserie: parseFloat(caseRates.karosserie) || 0,
        lackierung: parseFloat(caseRates.lackierung) || 0,
        elektrik: parseFloat(caseRates.elektrik) || 0,
      };
      const { data: contract } = await api.post(`/cases/${caseId}/dat/create-contract`, {
        hourly_rates: ratesPayload,
        force: forceNewContract,
        persist_rates: true,
      });
      setContractInfo(contract);
      targetUrlRef.current = contract.contract_url;
      if (contract.reused) {
        toast.message(`Bestehender DAT-Vorgang #${contract.contract_id} — direkt zur Kalkulation.`);
      } else {
        toast.success(`DAT-Vorgang #${contract.contract_id} angelegt — alle Daten übertragen, direkt zur Kalkulation.`);
      }

      // — Step 2: SSO launch ───────────────────────────────────────
      const { data: sso } = await api.post(`/dat/sso-launch`);

      // — Step 3: build auto-submit HTML ───────────────────────────
      const e = escapeHTML;
      const html = `<!doctype html><html lang="de"><head>
<meta charset="utf-8"><title>DAT wird geladen…</title>
<style>html,body{margin:0;padding:0;height:100%;background:#fafafa;font-family:system-ui,-apple-system,sans-serif;color:#52525b}
.wrap{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:14px}
.spinner{width:38px;height:38px;border:3px solid #e4e4e7;border-top-color:#06b6d4;border-radius:50%;animation:spin 0.7s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
small{color:#a1a1aa;font-size:11px;letter-spacing:0.1em;text-transform:uppercase}</style></head>
<body>
<div class="wrap"><div class="spinner"></div><div>SilverDAT calculateExpert wird gestartet…</div>
<small>Auto-Login als ${e(sso.login)} · Vorgang #${e(contract.contract_id)}</small></div>
<form id="ssoform" method="POST" action="${e(sso.url)}" style="display:none">
  <input type="hidden" name="${e(sso.transmission_key)}" value="${e(sso.token)}">
  <input type="hidden" name="customerNumber" value="${e(sso.customer_number)}">
  <input type="hidden" name="login" value="${e(sso.login)}">
</form>
<script>document.getElementById('ssoform').submit();</script>
</body></html>`;
      setIframeDoc(html);
    } catch (err) {
      const msg = apiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [caseId, caseRates]);

  /**
   * After the SSO form has loaded eventList.html inside the iframe,
   * JSESSIONID is set for www.dat.de. We can now navigate the iframe
   * directly to contractOpening.tmpl?cid=<cid> — DAT will recognise
   * the session and render the pre-filled contract.
   */
  const handleIframeLoad = useCallback(() => {
    if (!targetUrlRef.current || navigatedRef.current) return;
    // First load = the srcDoc form; second load = eventList.html post-SSO.
    // We wait for the second load (when iframe.contentWindow.location is on dat.de domain).
    // Cross-origin: we can't read location, but we can rely on the load event firing
    // AFTER the form has POSTed.
    if (!iframeRef.current) return;
    // Mark navigated so we don't loop
    navigatedRef.current = true;
    // Use a tiny delay so DAT can set cookies + finish initial render
    setTimeout(() => {
      try {
        iframeRef.current.src = targetUrlRef.current;
      } catch {
        /* ignored — cross-origin */
      }
    }, 600);
  }, []);

  // Listen for postMessage events from the DAT iframe.
  /**
   * Pull equipment + calculation back from DAT into the EG case.
   *
   * @param {Object} opts
   * @param {boolean} opts.silent  — wenn true, keine Erfolgs-Toasts, nur
   *     stille Übernahme. Wird vom Auto-Sync-Polling verwendet.
   * @param {boolean} opts.skipReload — wenn true, kein Window-Reload nach
   *     dem Pull. Verhindert Reload-Loops beim Auto-Sync.
   */
  const pullBack = useCallback(async (opts = {}) => {
    if (!contractInfo?.contract_id) return null;
    const silent = opts.silent === true;
    const skipReload = opts.skipReload === true || silent;
    if (!silent) setPulling(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/dat/pull-back`);
      const eqCount = data.equipment_count || 0;
      const partsCount = data.spare_parts_count || 0;
      const labourCount = data.labour_count || 0;
      const valCount = (data.valuation_fields || []).length;
      const calcKeys = (data.calculation_fields || []).length;
      const totalChanges = eqCount + partsCount + labourCount + valCount + calcKeys;
      if (!silent) {
        if (totalChanges === 0) {
          toast.info("DAT-Vorgang enthält noch keine Daten. Bitte erst Fahrzeug auswählen, kalkulieren oder bewerten.");
        } else {
          const parts = [];
          if (eqCount) parts.push(`${eqCount} Ausstattungen`);
          if (partsCount) parts.push(`${partsCount} Ersatzteile`);
          if (labourCount) parts.push(`${labourCount} Arbeitslohn`);
          if (valCount) parts.push(`${valCount} Bewertungsfelder`);
          if (calcKeys && !partsCount && !labourCount) parts.push(`${calcKeys} Summen`);
          toast.success(`Aus DAT übernommen: ${parts.join(" · ")}`);
        }
        const warnings = data.sanity_warnings || [];
        if (warnings.length > 0) {
          toast.warning(`${warnings.length} Bewertungs-Wert(e) wegen Plausibilität verworfen — bitte prüfen`, {
            description: warnings.join("\n"),
          });
        }
      }
      if (!skipReload) {
        setTimeout(() => window.location.reload(), 800);
      }
      return { totalChanges, data };
    } catch (e) {
      if (!silent) toast.error(apiError(e));
      return null;
    } finally {
      if (!silent) setPulling(false);
    }
  }, [caseId, contractInfo]);

  // ──────────────────────────────────────────────────────────────────
  // Phase-3-Auto-Sync: drei kombinierte Trigger, damit Daten aus DAT
  // ohne Klick im Hintergrund in die Akte fließen, sobald der Admin
  // im Iframe kalkuliert hat.
  //
  //   (a) DAT sendet `calculation_saved` postMessage → sofortiger Pull
  //   (b) Stiller Hintergrund-Pull alle 45 s solange Iframe sichtbar ist
  //       (Failsafe, falls DAT das postMessage nicht zuverlässig liefert)
  //   (c) Beim Schließen/Wechseln des Iframes → finaler Pull
  //
  // Alle drei sind idempotent. Pull-Back ist serverseitig Replace-by-Type
  // für die DAT-PDF, und der Subdoc-Merge sorgt dafür, dass bestehende
  // manuelle Felder nicht überschrieben werden.
  // ──────────────────────────────────────────────────────────────────
  const lastChangeHashRef = useRef("");
  const autoSyncRef = useRef(null);
  // Hold latest pullBack in a ref so the postMessage handler closure
  // and the unmount cleanup always call the freshest version.
  const pullBackRef = useRef(pullBack);
  useEffect(() => { pullBackRef.current = pullBack; }, [pullBack]);

  // (a) + (b): Aktiv solange iframeDoc gesetzt (= Vorgang läuft)
  useEffect(() => {
    if (!iframeDoc || !contractInfo?.contract_id) return;
    // (b) Polling-Loop
    const tick = async () => {
      const result = await pullBackRef.current({ silent: true, skipReload: true });
      if (!result) return;
      // Wenn sich der Daten-Snapshot geändert hat → Toast + UI-Reload
      const hash = JSON.stringify([
        result.data.equipment_count,
        result.data.spare_parts_count,
        result.data.labour_count,
        result.data.calculation_fields?.length,
      ]);
      if (hash !== lastChangeHashRef.current && lastChangeHashRef.current !== "") {
        toast.success("Neue DAT-Daten automatisch übernommen", {
          description: "Kalkulation, Positionen und Bewertung wurden in die Akte gespeichert.",
        });
        // Doku-Step + Co. brauchen frische Daten — sanfter Reload.
        setTimeout(() => window.location.reload(), 1200);
      }
      lastChangeHashRef.current = hash;
    };
    // Erstes Tick nach 5s (Zeit für DAT, Daten zu speichern), danach alle 45s
    const initial = setTimeout(tick, 5000);
    autoSyncRef.current = setInterval(tick, 45_000);
    return () => {
      clearTimeout(initial);
      if (autoSyncRef.current) clearInterval(autoSyncRef.current);
      autoSyncRef.current = null;
    };
  }, [iframeDoc, contractInfo?.contract_id]);

  // (c) Final-Pull beim Unmount (z.B. wenn Admin den Step wechselt)
  useEffect(() => {
    return () => {
      if (contractInfo?.contract_id) {
        // Fire-and-forget — Component ist eh weg
        pullBackRef.current({ silent: true, skipReload: true });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (ev) => {
      const origin = (ev.origin || "").toLowerCase();
      if (!origin.includes("datgroup.com") && !origin.includes("dat.de")) return;
      try {
        const payload = typeof ev.data === "string" ? JSON.parse(ev.data) : ev.data;
        // eslint-disable-next-line no-console
        console.log("[DAT calculateExpert event]", payload);
        if (payload?.event === "calculation_saved" || payload?.action === "saved") {
          // (a) Sofort Pull-Back triggern — der Admin hat in DAT auf
          // „Speichern" geklickt, also sind die Daten frisch.
          toast.success("Kalkulation in DAT calculateExpert gespeichert — übernehme Daten…");
          pullBackRef.current({ silent: false, skipReload: false });
        }
      } catch {
        /* ignore non-JSON messages */
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Manueller Upload: User hat in DAT „Drucken" geklickt, die Original-DAT-PDF
  // lokal gespeichert und lädt sie hier hoch. Sie ersetzt die auto-generierte
  // dat_calculation-PDF (Replace-by-Type macht der Backend-Endpoint).
  const onUploadCalcPdf = useCallback(async (file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") {
      toast.error("Bitte eine PDF-Datei wählen");
      return;
    }
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append("files", file);
      fd.append("doc_type", "dat_calculation");
      const { data } = await api.post(`/files/cases/${caseId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newDoc = (data?.documents || [])[0];
      if (newDoc) setCalcPdf(newDoc);
      toast.success("DAT-Kalkulations-PDF gespeichert", {
        description: 'Step „Kalkulation" ist jetzt erledigt ✓',
      });
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(apiError(e, "Upload fehlgeschlagen"));
    } finally {
      setUploadingPdf(false);
    }
  }, [caseId]);

  // Auto-Generation aus DAT-Daten (existierender Backend-Endpoint).
  const onGenerateCalcPdf = useCallback(async () => {
    setGeneratingPdf(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/dat/generate-calculation-pdf`);
      if (data?.document) setCalcPdf(data.document);
      toast.success("DAT-Kalkulations-PDF erzeugt", {
        description: 'Step „Kalkulation" ist jetzt erledigt ✓',
      });
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(apiError(e, "PDF konnte nicht erzeugt werden"));
    } finally {
      setGeneratingPdf(false);
    }
  }, [caseId]);

  // Kostenkontrolle: Nur Admin/Backoffice sehen den DAT-Kalkulations-Block.
  // Für Gutachter/Franchise wird das gesamte Modul komplett ausgeblendet —
  // kein Lock-Banner, kein deaktivierter Button. Der CaseEditor rendert den
  // Calculation-Step für diese Rollen bereits über CalculationStep.jsx.
  if (!canLaunch) return null;

  const ratesEditable = (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3" data-testid="dat-rates-editor">
      <RateField label="Mechanik" value={caseRates.mechanik}
        onChange={(v) => setCaseRates((r) => ({ ...r, mechanik: v }))} defaultV={defaultRates?.mechanik} />
      <RateField label="Karosserie" value={caseRates.karosserie}
        onChange={(v) => setCaseRates((r) => ({ ...r, karosserie: v }))} defaultV={defaultRates?.karosserie} />
      <RateField label="Lackierung" value={caseRates.lackierung}
        onChange={(v) => setCaseRates((r) => ({ ...r, lackierung: v }))} defaultV={defaultRates?.lackierung} />
      <RateField label="Elektrik" value={caseRates.elektrik}
        onChange={(v) => setCaseRates((r) => ({ ...r, elektrik: v }))} defaultV={defaultRates?.elektrik} />
    </div>
  );

  return (
    <div className="rounded-2xl border-2 border-cyan-200 bg-gradient-to-br from-cyan-50/60 via-white to-white overflow-hidden" data-testid="dat-calculate-expert-block">
      <div className="px-5 py-4 border-b border-cyan-100 flex items-center gap-3 flex-wrap">
        <div className="h-9 w-9 rounded-xl brand-gradient-bg flex items-center justify-center shrink-0">
          <Calculator className="h-4 w-4 text-white" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700">SilverDAT calculateExpert</div>
          <div className="font-display font-semibold text-base">Schadenkalkulation in DAT</div>
          <div className="text-xs text-zinc-500 mt-0.5">
            Vorgang wird automatisch mit allen Daten dieser Akte angelegt — Halter, Fahrzeug, Versicherung, Stundenlöhne.
            {loginInfo && <> Konto: <strong className="text-zinc-700">Kd-Nr {loginInfo.customer_number} · {loginInfo.username}</strong></>}
          </div>
        </div>
        <div className="flex gap-2">
          {iframeDoc && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setExpanded((v) => !v)}
                data-testid="dat-calc-expand-btn"
                title={expanded ? "Standardgröße" : "Vollbild"}
              >
                <Maximize2 className="h-3.5 w-3.5 mr-1.5" />
                {expanded ? "Verkleinern" : "Vollbild"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => launch(false)}
                disabled={loading}
                data-testid="dat-calc-reload-btn"
                title="Session neu starten"
              >
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
              </Button>
            </>
          )}
          {!iframeDoc && (
            <Button
              type="button"
              onClick={() => launch(false)}
              disabled={loading}
              className="brand-gradient-bg text-white"
              data-testid="dat-calc-open-btn"
            >
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
              ) : (
                <Calculator className="h-3.5 w-3.5 mr-2" />
              )}
              Vorgang in DAT anlegen + öffnen
            </Button>
          )}
        </div>
      </div>

      {/* Copy-Helper: Eckdaten zum Kopieren — hilft bei DAT-Auto-Fill-Problemen.
          Sichtbar IMMER (vor + während + nach Iframe-Öffnung), damit der User
          jederzeit Werte rauskopieren kann. */}
      {caseDoc && <DATCopyHelper case={caseDoc} defaultOpen={false} />}

      {/* Hourly rates editor (pre-launch) */}
      {!iframeDoc && (
        <div className="px-5 py-4 bg-cyan-50/40 border-b border-cyan-100">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm font-semibold text-cyan-900">
              <Wrench className="h-4 w-4" />
              Stundenlöhne für diesen Vorgang
              <span className="text-[10px] font-normal text-cyan-700 tracking-wider uppercase ml-1">
                {showRates ? "" : "(Default aus Einstellungen)"}
              </span>
            </div>
            <Button type="button" variant="outline" size="sm"
              onClick={() => setShowRates((v) => !v)} className="border-cyan-300 text-cyan-700"
              data-testid="dat-rates-toggle-btn">
              <Settings2 className="h-3.5 w-3.5 mr-1.5" />
              {showRates ? "Zuklappen" : "Bearbeiten"}
            </Button>
          </div>
          {showRates ? ratesEditable : (
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-600 mt-2">
              <span>Mechanik: <strong>{caseRates.mechanik || "—"} €</strong></span>
              <span>Karosserie: <strong>{caseRates.karosserie || "—"} €</strong></span>
              <span>Lack: <strong>{caseRates.lackierung || "—"} €</strong></span>
              <span>Elektrik: <strong>{caseRates.elektrik || "—"} €</strong></span>
            </div>
          )}
        </div>
      )}

      {/* Contract status badge + Step-Switcher */}
      {contractInfo && (
        <>
          <div className="px-5 py-2.5 bg-emerald-50/70 border-b border-emerald-200 flex items-center gap-3 flex-wrap text-xs">
            <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0" />
            <span className="font-semibold text-emerald-900">
              DAT-Vorgang #{contractInfo.contract_id} {contractInfo.reused ? "(bestehend)" : "(neu angelegt)"}
            </span>
            <a href={contractInfo.contract_url} target="_blank" rel="noreferrer"
              className="text-emerald-700 underline inline-flex items-center gap-1 hover:text-emerald-900"
              data-testid="dat-contract-external-link">
              <ExternalLink className="h-3 w-3" />
              In neuem Tab öffnen
            </a>
            <Button type="button" size="sm" variant="outline"
              onClick={pullBack} disabled={pulling}
              className="ml-auto h-7 text-xs border-cyan-300 text-cyan-700 hover:bg-cyan-100"
              data-testid="dat-pull-back-btn">
              {pulling ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
              Daten aus DAT zurückholen
            </Button>
            {iframeDoc && (
              <Button type="button" size="sm" variant="ghost"
                onClick={() => launch(true)}
                disabled={loading}
                className="h-7 text-xs text-rose-600 hover:bg-rose-50"
                data-testid="dat-contract-new-btn">
                Neuen Vorgang anlegen
              </Button>
            )}
          </div>
          {iframeDoc && contractInfo.deep_links && (
            <DATStepSwitcher
              deepLinks={contractInfo.deep_links}
              active={activeStep}
              onSwitch={(stepKey) => {
                const url = contractInfo.deep_links[stepKey];
                if (!url || !iframeRef.current) return;
                setActiveStep(stepKey);
                navigatedRef.current = true;
                iframeRef.current.src = url;
              }}
            />
          )}
        </>
      )}

      {/* DAT-Kalkulations-PDF — Status + Upload/Generate (immer sichtbar) ─── */}
      <div className="px-5 py-3 bg-sky-50/60 border-b border-sky-200 flex items-center gap-3 flex-wrap text-xs" data-testid="dat-calc-pdf-panel">
        <FileText className="h-4 w-4 text-sky-700 shrink-0" />
        {calcPdf ? (
          <>
            <span className="font-semibold text-sky-900 inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" data-testid="dat-calc-pdf-check" />
              Kalkulations-PDF angehängt
            </span>
            <span className="text-zinc-500 truncate max-w-[280px]" title={calcPdf.original_filename}>
              {calcPdf.original_filename}
            </span>
            <a
              href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/files/download/${encodeURI(calcPdf.storage_path)}?auth=${encodeURIComponent(typeof localStorage !== "undefined" ? (localStorage.getItem("access_token") || "") : "")}`}
              target="_blank" rel="noreferrer"
              className="text-sky-700 underline inline-flex items-center gap-1 hover:text-sky-900"
              data-testid="dat-calc-pdf-download"
            >
              <Download className="h-3 w-3" /> Öffnen
            </a>
          </>
        ) : (
          <span className="font-semibold text-sky-900">Noch keine Kalkulations-PDF angehängt</span>
        )}
        <div className="ml-auto flex gap-2">
          <input
            ref={calcPdfInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => onUploadCalcPdf(e.target.files?.[0])}
            data-testid="dat-calc-pdf-upload-input"
          />
          <Button
            type="button" size="sm" variant="outline"
            onClick={() => calcPdfInputRef.current?.click()}
            disabled={uploadingPdf}
            className="h-7 text-xs border-sky-300 text-sky-700 hover:bg-sky-100"
            data-testid="dat-calc-pdf-upload-btn"
            title={'In DAT auf „Drucken & Senden" → PDF speichern → hier hochladen'}
          >
            {uploadingPdf ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
            DAT-Original-PDF hochladen
          </Button>
          <Button
            type="button" size="sm" variant="ghost"
            onClick={onGenerateCalcPdf}
            disabled={generatingPdf || !contractInfo?.contract_id}
            className="h-7 text-xs text-sky-700 hover:bg-sky-100"
            data-testid="dat-calc-pdf-generate-btn"
            title={contractInfo?.contract_id ? "Erzeugt eine PDF aus den DAT-Daten dieser Akte" : "Zuerst DAT-Vorgang anlegen und Daten zurückholen"}
          >
            {generatingPdf ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCcw className="h-3 w-3 mr-1" />}
            Aus DAT-Daten erzeugen
          </Button>
        </div>
      </div>

      {/* Vehicle context summary (only before iframe is loaded) */}
      {!iframeDoc && (vehicle?.vin || vehicle?.license_plate) && (
        <div className="px-5 py-2.5 bg-zinc-50/50 border-b border-zinc-100 text-xs text-zinc-600 flex flex-wrap gap-x-5 gap-y-1">
          {vehicle?.license_plate && <span><strong className="text-zinc-800">Kennzeichen:</strong> {vehicle.license_plate}</span>}
          {vehicle?.vin && <span><strong className="text-zinc-800">VIN:</strong> {vehicle.vin}</span>}
          {(vehicle?.make || vehicle?.model) && <span><strong className="text-zinc-800">Fahrzeug:</strong> {vehicle.make} {vehicle.model}</span>}
        </div>
      )}

      {/* iframe / placeholder / error */}
      <div className="bg-white">
        {error && (
          <div className="p-6 text-sm text-rose-700 bg-rose-50 border-t border-rose-200 flex items-start gap-3" data-testid="dat-calc-error">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <div>
              <div className="font-semibold">DAT-Anbindung fehlgeschlagen</div>
              <div className="mt-0.5">{error}</div>
              <div className="mt-2 text-xs text-rose-600">
                Bitte unter <strong>Einstellungen → API-Zugänge → DAT-Zugang</strong> Kundennummer, Benutzer, Passwort und Schnittstellen-Signatur prüfen.
              </div>
            </div>
          </div>
        )}

        {!iframeDoc && !error && (
          <div className="p-10 text-center text-sm text-zinc-500" data-testid="dat-calc-empty">
            Klicke auf <strong>{'„Vorgang in DAT anlegen + öffnen"'}</strong>.
            <div className="text-xs text-zinc-400 mt-1">
              Wir legen den Vorgang per SOAP automatisch in DAT an und öffnen ihn direkt im Iframe — alle Felder bereits ausgefüllt.
            </div>
          </div>
        )}

        {iframeDoc && (
          <iframe
            ref={iframeRef}
            srcDoc={iframeDoc}
            onLoad={handleIframeLoad}
            title="DAT calculateExpert"
            className="w-full block"
            style={{ height: expanded ? "85vh" : "780px", border: 0 }}
            allow="clipboard-read; clipboard-write; camera; microphone; geolocation"
            data-testid="dat-calc-iframe"
          />
        )}
      </div>
    </div>
  );
}


function RateField({ label, value, onChange, defaultV }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-cyan-800">{label}</Label>
      <div className="relative mt-1">
        <Input type="number" inputMode="decimal" min="0" step="0.5"
          value={value} onChange={(e) => onChange(e.target.value)}
          className="pr-9 bg-white" data-testid={`rate-input-${label.toLowerCase()}`} />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none">€</span>
      </div>
      {defaultV && value !== String(defaultV) && (
        <button type="button" onClick={() => onChange(String(defaultV))}
          className="text-[10px] text-zinc-400 mt-1 hover:text-zinc-600 underline"
          data-testid={`rate-reset-${label.toLowerCase()}`}>
          → Default {defaultV} €
        </button>
      )}
    </div>
  );
}


/**
 * DATStepSwitcher — Tab-Leiste oberhalb des Iframes, die zwischen den
 * DAT-Workflow-Steps wechselt. Default-Tab ist „Kalkulation" — der Admin
 * landet sofort dort, wo er produktiv wird, und muss nicht durch die
 * Vorgangs-Anlage klicken.
 */
function DATStepSwitcher({ deepLinks, active, onSwitch }) {
  // Reihenfolge gemäß DAT-Workflow-Logik
  const steps = [
    { key: "calculation", label: "Kalkulation", testid: "dat-step-calculation" },
    { key: "calculation_result", label: "Ergebnis", testid: "dat-step-result" },
    { key: "hourly_rates", label: "Stundenlöhne", testid: "dat-step-rates" },
    { key: "equipment", label: "Ausstattung", testid: "dat-step-equipment" },
    { key: "vehicle_select", label: "Fahrzeug", testid: "dat-step-vehicle" },
    { key: "contract_opening", label: "Halter / Stamm", testid: "dat-step-contract" },
    { key: "print_send", label: "Drucken & Senden", testid: "dat-step-print" },
  ];
  return (
    <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex flex-wrap gap-1" data-testid="dat-step-switcher">
      {steps.map((s) => {
        const isActive = active === s.key;
        const enabled = !!deepLinks[s.key];
        return (
          <button
            key={s.key}
            type="button"
            disabled={!enabled}
            onClick={() => enabled && onSwitch(s.key)}
            data-testid={s.testid}
            className={[
              "px-3 py-1 text-[11px] rounded-md font-semibold transition-colors",
              isActive
                ? "brand-gradient-bg text-white shadow"
                : enabled
                ? "bg-white border border-zinc-200 text-zinc-700 hover:border-cyan-300 hover:text-cyan-700"
                : "bg-zinc-100 text-zinc-400 cursor-not-allowed",
            ].join(" ")}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
}
